import React, { useEffect, useState, useRef } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
interface Option {
  value: string;
  label: string;
}
interface SearchableSelectProps {
  label?: string;
  value?: string;
  onChange: (e: {
    target: {
      value: string;
      name?: string;
    };
  }) => void;
  options: Option[];
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  className?: string;
  description?: string;
  fullWidth?: boolean;
}
export function SearchableSelect({
  label,
  value,
  onChange,
  options,
  placeholder = 'Seleziona...',
  error,
  disabled = false,
  required = false,
  name,
  className = '',
  description,
  fullWidth = true
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectedOption = options.find(opt => opt.value === value);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const filteredOptions = options.filter(opt => opt.label.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleSelect = (optionValue: string) => {
    onChange({
      target: {
        value: optionValue,
        name
      }
    });
    setIsOpen(false);
    setSearchTerm('');
  };
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange({
      target: {
        value: '',
        name
      }
    });
    setSearchTerm('');
  };
  return <div ref={containerRef} className={`${fullWidth ? 'w-full' : ''} flex flex-col gap-1.5`} data-id="element-2013" data-mp="198co89am5m0i">
      {label && <label className="block text-sm font-medium text-slate-700" data-id="element-2014" data-mp="1gccey3gdqzt4">
          {label} {required && <span className="text-red-500" data-id="element-2015" data-mp="1la44wrxgsbuo">*</span>}
        </label>}
      <div className="relative" data-id="element-2016" data-mp="1q7nx6vqzyp5m">
        <div onClick={() => !disabled && setIsOpen(!isOpen)} className={`
            relative flex items-center w-full rounded-xl border-2 border-slate-200 bg-slate-50/50 
            pl-4 pr-10 py-2.5 text-sm transition-all duration-200 cursor-pointer
            ${isOpen ? 'border-seform-orange bg-white ring-0' : 'hover:border-slate-300'}
            ${disabled ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''}
            ${error ? 'border-red-300 focus:border-red-500' : ''}
            ${className}
          `} data-id="element-2017" data-mp="1q7e6t721ifze">
          <span className={`block truncate ${!selectedOption ? 'text-slate-400' : 'text-slate-900'}`} data-id="element-2018" data-mp="elxmszpctxk4">
            {selectedOption ? selectedOption.label : placeholder}
          </span>

          <div className="absolute inset-y-0 right-0 pr-3 flex items-center gap-1" data-id="element-2019" data-mp="2wvjzzcq0mnwr">
            {selectedOption && !disabled && <div onClick={handleClear} className="p-1 hover:bg-slate-200 rounded-full text-slate-400 hover:text-slate-600 transition-colors" data-id="element-2020" data-mp="2s1p99pbzt8o6">
                <X className="h-3 w-3" data-id="element-2021" data-mp="2j4yhhp4h4fze" />
              </div>}
            <div className="pointer-events-none text-slate-400" data-id="element-2022" data-mp="3fscvzgqki2us">
              <ChevronDown className="h-4 w-4" data-id="element-2023" data-mp="3bs2fvb929w27" />
            </div>
          </div>
        </div>

        {isOpen && !disabled && <div className="absolute z-50 mt-1 w-full rounded-xl bg-white shadow-lg border border-slate-100 py-1 overflow-hidden animate-in fade-in zoom-in-95 duration-100" data-id="element-2024" data-mp="36rn58rg5z2nx">
            <div className="px-2 py-2 border-b border-slate-50" data-id="element-2025" data-mp="1ve2h43lyccgj">
              <div className="relative" data-id="element-2026" data-mp="1vbgaq7ass2zw">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400" data-id="element-2027" data-mp="1qe6ot1mma9gt">
                  <Search className="h-3.5 w-3.5" data-id="element-2028" data-mp="3eacjwvh7j2b7" />
                </div>
                <input ref={inputRef} type="text" className="block w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-3 py-1.5 text-xs focus:border-seform-orange focus:ring-0 focus:outline-none" placeholder="Cerca..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} autoFocus onClick={e => e.stopPropagation()} data-id="element-2029" data-mp="3j2e5x946g7ef" />
              </div>
            </div>
            <div className="max-h-60 overflow-auto custom-scrollbar" data-id="element-2030" data-mp="3o0fkbgqnrijk">
              {filteredOptions.length > 0 ? filteredOptions.map(opt => <div key={opt.value} onClick={() => handleSelect(opt.value)} className={`
                      px-4 py-2 text-sm cursor-pointer transition-colors
                      ${value === opt.value ? 'bg-orange-50 text-seform-orange font-medium' : 'text-slate-700 hover:bg-slate-50'}
                    `} data-id="element-2031" data-mp="2cliu43iqmnam">
                    {opt.label}
                  </div>) : <div className="px-4 py-3 text-xs text-slate-400 text-center" data-id="element-2032" data-mp="2e2fylx9udql9">
                  Nessun risultato trovato
                </div>}
            </div>
          </div>}
      </div>
      {description && !error && <p className="text-xs text-slate-500" data-id="element-2033" data-mp="1mdmgtv3sk59k">{description}</p>}
      {error && <p className="text-xs text-red-600 font-medium" data-id="element-2034" data-mp="203ybo1rrgi4e">{error}</p>}
    </div>;
}